import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { PublicShell } from '@ahora-patagonia/contracts';

export interface PublicFooterProps {
  shell: PublicShell | null;
}

/**
 * El pie del diario. Dos reglas de accesibilidad que no se negocian (handoff §4):
 *
 *  - **Cada enlace de texto lleva 48 px de alto propio** (`min-height` + flex): un enlace
 *    de 22 px de alto es la falla táctil más común de un pie de diario.
 *  - Los íconos de redes heredan el color del texto del pie con `currentColor` (nunca
 *    `brandColor`: seis colores de marca compiten con el del cliente), glifo de 20 px
 *    centrado en un área táctil de 48×48 invisible, sin caja ni borde.
 *
 * El SVG de cada red viene del catálogo del sistema (`SocialNetwork.icon`) y se inyecta tal
 * cual: es contenido del sistema, no de lectores.
 */
const esCorreo = (href: string): boolean => href.toLowerCase().startsWith('mailto:');

export function PublicFooter({ shell }: PublicFooterProps) {
  // Todas las secciones, elegidas o no para la cabecera: el pie es el índice del sitio.
  const secciones = shell?.nav ?? [];
  const redes = shell?.socialLinks ?? [];

  return (
    <Box
      component="footer"
      sx={{
        // Pie en el color del cliente, igual que la cabecera: cierran la página con la marca.
        // Ya no hace falta la regla de 2 px que lo separaba del ground: lo separa el color.
        backgroundColor: 'var(--color-cliente)',
        color: 'var(--sobre-cliente)',
        mt: 4,
        '& a': { color: 'inherit' },
        '& a:hover': { color: 'inherit' },
      }}
    >
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: '14px', sm: '20px', lg: '32px' }, py: 3 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.015em' }}>
          {shell?.tenant.nombre ?? ''}
        </Typography>

        <Box
          component="nav"
          aria-label="Secciones"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
            columnGap: 2,
            mt: 1,
          }}
        >
          {secciones.map((s) => (
            <MuiLink
              key={s.key}
              component={RouterLink}
              to={s.path}
              sx={{ display: 'flex', alignItems: 'center', minHeight: 48, fontSize: 14 }}
            >
              {s.name}
            </MuiLink>
          ))}
        </Box>

        {redes.length > 0 && (
          <Box sx={{ display: 'flex', ml: '-14px', mt: 1 }}>
            {redes.map((red) => (
              <MuiLink
                key={red.key}
                href={red.url}
                // El correo no abre pestaña: `target="_blank"` con un `mailto:` deja una pestaña en
                // blanco abierta al costado del cliente de correo. Se detecta por el esquema del
                // valor y no por la clave: lo que decide el comportamiento del enlace es el `href`.
                {...(esCorreo(red.url) ? {} : { target: '_blank', rel: 'noopener' })}
                aria-label={red.name}
                sx={{
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'inherit',
                  // El glifo del catálogo hereda por currentColor; se fuerza el tamaño de 20.
                  '& svg': { width: 20, height: 20 },
                }}
                // SVG del catálogo global del sistema (SocialNetwork.icon), no contenido de
                // usuarios: es el único punto del sitio que inyecta marcado externo.
                dangerouslySetInnerHTML={{ __html: red.icon }}
              />
            ))}
          </Box>
        )}

        <Typography sx={{ color: 'inherit', opacity: 0.78, fontSize: 12, mt: 2 }}>
          © {new Date().getFullYear()} {shell?.tenant.nombre ?? ''}. Las direcciones de las
          notas no cambian: se pueden citar y compartir.
        </Typography>

        {/*
          La firma de quien hace el software. Va **debajo** del copyright del medio y más apagada
          que él: en un producto white-label la página es del diario, y nuestra firma es una nota al
          pie, no una marca compartida.

          `variant="firma"` y no un `fontSize` en el `sx`: las variantes del theme traen sus propios
          tamaños por corte (`body1` es `escala(15, 15, 16)`), y esas media queries **pisan** un
          `fontSize` suelto de arriba de 600px. Un `sx={{ fontSize: 11 }}` acá se vería de 11px solo
          en un teléfono y de 16px en escritorio, que es justo donde tiene que pasar desapercibida.

          Texto y no enlace, a propósito: un enlace acá se lleva un lugar en el orden de tabulación
          del pie de todas las páginas del diario, y saca al lector del sitio de un cliente. Si algún
          día tiene que ser enlace, primero hay una URL que acordar.
        */}
        <Typography variant="firma" sx={{ color: 'inherit', opacity: 0.55, mt: 1 }}>
          Software provisto por Nubira TSS
        </Typography>
      </Box>
    </Box>
  );
}
